import React from 'react';

function safeSvgId(hookId: string): string {
  return `rhsn${hookId.replace(/[^a-zA-Z0-9]/g, '')}`;
}

/**
 * Decorative hero-only SVG: curved routes, nodes, one slow pulse.
 * Sits behind copy / product; pointer-events: none.
 */
export function RelixHeroSignalNetwork({
  className = '',
}: {
  className?: string;
}): React.ReactElement {
  const uid = safeSvgId(React.useId());
  const gradLine = `${uid}-hl`;
  const gradActive = `${uid}-ha`;
  const gradNode = `${uid}-hn`;
  const gradBridge = `${uid}-hb`;
  const filterGlow = `${uid}-fg`;

  return (
    <svg
      className={['relix-hero-signal-network', className].filter(Boolean).join(' ')}
      viewBox="0 0 1200 720"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradLine} x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="rgba(124, 92, 255, 0.06)" />
          <stop offset="45%" stopColor="rgba(167, 139, 250, 0.28)" />
          <stop offset="100%" stopColor="rgba(59, 130, 246, 0.08)" />
        </linearGradient>
        <linearGradient id={gradBridge} x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="rgba(167, 139, 250, 0)" />
          <stop offset="38%" stopColor="rgba(200, 190, 255, 0.11)" />
          <stop offset="100%" stopColor="rgba(99, 102, 241, 0.05)" />
        </linearGradient>
        <linearGradient
          id={gradActive}
          gradientUnits="userSpaceOnUse"
          x1="180"
          y1="360"
          x2="1080"
          y2="360"
        >
          <stop offset="0%" stopColor="transparent" />
          <stop offset="42%" stopColor="rgba(255,255,255,0.04)" />
          <stop offset="50%" stopColor="rgba(226, 220, 255, 0.55)" />
          <stop offset="58%" stopColor="rgba(255,255,255,0.04)" />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
        <radialGradient id={gradNode} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(238, 232, 255, 0.38)" />
          <stop offset="70%" stopColor="rgba(167, 139, 250, 0.12)" />
          <stop offset="100%" stopColor="rgba(123, 92, 255, 0)" />
        </radialGradient>
        <filter id={filterGlow} x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Very faint routes tying hero copy column toward product preview — atmosphere only */}
      <g className="relix-hero-signal-network__hero-bridge" opacity="1">
        <path
          d="M 88 288 Q 340 252 620 228"
          stroke={`url(#${gradBridge})`}
          strokeWidth="0.85"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          opacity="0.42"
        />
        <path
          d="M 132 392 Q 410 332 688 276"
          stroke={`url(#${gradBridge})`}
          strokeWidth="0.65"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          opacity="0.32"
        />
        <circle cx="518" cy="246" r="1.15" fill="rgba(226, 220, 255, 0.14)" opacity="0.55" />
        <circle cx="412" cy="318" r="0.85" fill="rgba(186, 174, 255, 0.12)" opacity="0.45" />
      </g>

      <g className="relix-hero-signal-network__paths" opacity="0.88">
        <path
          className="hero-signal-line hero-signal-line--soft"
          d="M 80 240 L 420 268 L 720 220"
          stroke={`url(#${gradLine})`}
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          opacity="0.4"
        />
        <path
          className="hero-signal-line hero-signal-line--active"
          d="M 160 380 C 360 300 520 260 740 240 S 980 300 1060 360"
          stroke={`url(#${gradActive})`}
          strokeWidth="1.35"
          strokeLinecap="round"
          filter={`url(#${filterGlow})`}
          opacity="0.72"
        />
        <path
          className="hero-signal-pulse"
          d="M 160 380 C 360 300 520 260 740 240 S 980 300 1060 360"
          stroke="rgba(232, 226, 255, 0.38)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="18 420"
          opacity="0.55"
          vectorEffect="non-scaling-stroke"
        />
      </g>

      <g className="relix-hero-signal-network__nodes">
        <circle
          className="hero-signal-node-glow"
          cx="720"
          cy="220"
          r="16"
          fill={`url(#${gradNode})`}
          opacity="0.42"
        />
        <circle
          className="hero-signal-node-ring"
          cx="720"
          cy="220"
          r="5.5"
          stroke="rgba(167, 139, 250, 0.42)"
          strokeWidth="1.1"
          fill="rgba(5, 9, 20, 0.92)"
        />
        <circle
          className="hero-signal-node-core"
          cx="720"
          cy="220"
          r="1.8"
          fill="rgba(232, 226, 255, 0.65)"
        />

        <circle
          className="hero-signal-node-glow hero-signal-node-wave"
          cx="900"
          cy="340"
          r="22"
          fill={`url(#${gradNode})`}
          opacity="0.5"
        />
        <circle
          className="hero-signal-node-ring"
          cx="900"
          cy="340"
          r="7"
          stroke="rgba(220, 210, 255, 0.5)"
          strokeWidth="1.25"
          fill="rgba(5, 9, 20, 0.94)"
        />
        <circle
          className="hero-signal-node-core"
          cx="900"
          cy="340"
          r="2.2"
          fill="rgba(232, 226, 255, 0.82)"
        />
      </g>
    </svg>
  );
}
